import { format } from 'date-fns';
import { clamp } from 'lodash';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
} from 'react';
import { useTransformContext } from 'react-zoom-pan-pinch';
import { downloadFile } from 'utils/download-file';

import { ImageViewerData } from './types';

interface Props<T> {
  allData: ImageViewerData<T>[];
  defaultIndex?: number;
}

export const useImageViewer = <T>({ allData, defaultIndex = 0 }: Props<T>) => {
  const {
    zoomIn: zoomInFnc,
    zoomOut: zoomOutFnc,
    centerView: centerViewFnc,
    resetTransform,
  } = useTransformContext();
  const initialIndex = useMemo(
    () => clamp(defaultIndex, 0, allData.length - 1),
    [allData.length, defaultIndex]
  );
  const [index, setIndex] = useState(initialIndex);
  const [rotation, setRotation] = useState(0);

  const data = useMemo<undefined | typeof allData[number]>(
    () => allData[index],
    [allData, index]
  );

  useEffect(() => {
    setIndex(defaultIndex);
  }, [defaultIndex]);

  // reset the transform whenever we go to a different image
  useEffect(() => {
    resetTransform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const goForward = useCallback(() => {
    setIndex((prev) => Math.min(prev + 1, allData.length - 1));
  }, [allData.length]);

  const goBackward = useCallback(() => {
    setIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToIndex = useCallback(
    (index: number) => {
      setIndex(clamp(index, 0, allData.length - 1));
    },
    [allData.length]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowRight') {
        goForward();
      }

      if (e.key === 'ArrowLeft') {
        goBackward();
      }
    },
    [goForward, goBackward]
  );

  const metadata = useMemo(
    () => ({
      goForwardDisabled: index === Math.max(0, allData.length - 1),
      goBackwardDisabled: index === 0,
      itemCount: allData.length,
    }),
    [allData.length, index]
  );

  const zoomIn = useCallback(() => zoomInFnc(), [zoomInFnc]);
  const zoomOut = useCallback(() => zoomOutFnc(), [zoomOutFnc]);
  const centerView = useCallback(() => centerViewFnc(), [centerViewFnc]);

  const rotateRight = useCallback(() => {
    setRotation((rotation + 90) % 360);
  }, [rotation]);

  const rotateLeft = useCallback(() => {
    const rawRotation = (rotation - 90) % 360;
    const offset = rawRotation < 0 ? 360 : 0;

    setRotation(rawRotation + offset);
  }, [rotation]);

  const downloadImage = useCallback(() => {
    if (!data) {
      return;
    }

    const date = new Date();
    const dateExtension = format(date, 'dd-MM-yyyy');

    downloadFile(
      data?.url,
      data?.label ?? `obrazek-bez-nazvu-${dateExtension}`
    );
  }, [data]);

  return useMemo(
    () => ({
      data,
      index,
      zoomIn,
      zoomOut,
      rotation,
      rotateRight,
      rotateLeft,
      centerView,
      goForward,
      goBackward,
      goToIndex,
      onKeyDown,
      metadata,
      downloadImage,
    }),
    [
      data,
      index,
      zoomIn,
      zoomOut,
      rotation,
      rotateRight,
      rotateLeft,
      centerView,
      goForward,
      goBackward,
      goToIndex,
      onKeyDown,
      metadata,
      downloadImage,
    ]
  );
};
